import { useState, useEffect, useContext, createContext } from 'react';
import { 
    onAuthStateChanged, 
    signInAnonymously, 
    signInWithPopup, 
    GoogleAuthProvider,
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword,
    updateProfile,
    signOut
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore'; // <--- IMPORTANTE: Para ler a role
import { auth, db } from '../firebase/config';    // <--- IMPORTANTE: Importar db

// Criamos o Contexto para partilhar o estado por toda a App
const AuthContext = createContext();

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [role, setRole] = useState(null); // <--- AQUI GUARDAMOS O CARGO (Admin/Editor)
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
            setLoading(true);
            
            if (currentUser) {
                // 1. UTILIZADOR ENTROU
                localStorage.removeItem('manual_logout');
                
                // --- LÓGICA DE DETEÇÃO DE CARGO (ROLE) ---
                let userRole = null;
                try {
                    // Tenta ler as "Claims" (permissões rápidas)
                    const tokenResult = await currentUser.getIdTokenResult();
                    userRole = tokenResult.claims.role;

                    // Se não tiver Claim, vai ler à base de dados users/{uid}
                    if (!userRole) {
                        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
                        if (userDoc.exists()) {
                            userRole = userDoc.data().role;
                        }
                    }
                    console.log("🔐 Login:", currentUser.email, "| Cargo:", userRole);
                } catch (err) {
                    console.error("Erro ao ler cargo:", err);
                }

                setUser(currentUser);
                setRole(userRole);
                setLoading(false);

            } else {
                // 2. NÃO HÁ UTILIZADOR (Logout ou Anónimo)
                setRole(null); // Limpa o cargo

                const userManuallyLoggedOut = localStorage.getItem('manual_logout') === 'true';

                if (userManuallyLoggedOut) {
                    // Saiu manualmente: Fica quieto (ecrã de login)
                    setUser(null);
                    setLoading(false);
                } else {
                    // Primeira visita: Cria conta anónima
                    try {
                        const anonUser = await signInAnonymously(auth);
                        // O onAuthStateChanged vai disparar de novo, não precisamos de fazer setUser aqui
                    } catch(err) {
                        console.error("Erro login anónimo:", err);
                        setUser(null);
                        setLoading(false);
                    }
                }
            }
        });

        return () => unsubscribe();
    }, []);

    // --- Funções de Autenticação ---

    const loginWithGoogle = async () => {
        setError(null);
        localStorage.removeItem('manual_logout');
        try {
            const provider = new GoogleAuthProvider();
            return await signInWithPopup(auth, provider);
        } catch (err) {
            console.error("Erro Google:", err);
            setError("Erro Google.");
            throw err;
        }
    };

    const loginWithEmail = async (email, password) => {
        setError(null);
        localStorage.removeItem('manual_logout');
        try {
            return await signInWithEmailAndPassword(auth, email, password);
        } catch (err) {
            console.error("Erro Email:", err);
            setError("Erro Login.");
            throw err;
        }
    };

    const registerWithEmail = async (email, password, name) => {
        setError(null);
        localStorage.removeItem('manual_logout');
        try {
            const res = await createUserWithEmailAndPassword(auth, email, password);
            await updateProfile(res.user, { displayName: name });
            // Criar documento na base de dados para definir role (opcional, default: cliente)
            // await setDoc(doc(db, 'users', res.user.uid), { role: 'cliente', email });
            return res;
        } catch (err) {
            console.error("Erro Registo:", err);
            setError("Erro Registo.");
            throw err;
        }
    };

    const logout = async () => {
        try {
            localStorage.setItem('manual_logout', 'true');
            setRole(null);
            await signOut(auth);
        } catch (err) {
            console.error("Erro Logout:", err);
        }
    };

    return (
        <AuthContext.Provider value={{ 
            user, 
            role, // <--- Agora exportamos a role para a App usar!
            loading, 
            error,
            loginWithGoogle, 
            loginWithEmail, 
            registerWithEmail, 
            logout 
        }}>
            {children}
        </AuthContext.Provider>
    );
}

// Hook para usar o contexto
export function useAuth() {
    return useContext(AuthContext);
}